import { createSlice } from "@reduxjs/toolkit";

import {
  exportWorkspaceByAreaThunk,
  exportWorkspaceByUserThunk,
  fetchWorkspaceByArea,
  fetchWorkspaceByUser,
  fetchWorkspaceHeader,
} from "./actions";

export const initialState = {
  workspaceId: null,
  header: null,
  headerIsLoading: false,
  headerError: null,
  byArea: null,
  byUser: null,
  filters: { range: "all", from: null, to: null, source: "all" },
  selectedUserNames: [],
  isExporting: false,
};

const DEFAULT_HEADER_ERROR = "Failed to load the workspace account header";

// A response for workspace A can resolve after the user has already navigated to workspace B
// (resetForWorkspace runs synchronously on navigation; the in-flight request for A is not
// cancelled). Without this check, A's header/by-area/by-user payload would render under B's URL —
// a wrong-account misread, not just stale data. `action.meta.arg` is the workspaceId the thunk was
// originally dispatched with (createAsyncThunk convention), compared against the workspace
// currently selected in state.
const isForCurrentWorkspace = (state, action) => action.meta.arg === state.workspaceId;

const cloudUsageWorkspaceDetailSlice = createSlice({
  name: "cloudUsageWorkspaceDetail",
  initialState,
  reducers: {
    resetForWorkspace: (state, action) => {
      const payload =
        action.payload !== null && typeof action.payload === "object"
          ? action.payload
          : { workspaceId: action.payload };
      const { workspaceId, range, from, to, source } = payload;
      return {
        ...initialState,
        workspaceId: workspaceId ?? null,
        filters: {
          range: range ?? initialState.filters.range,
          from: from ?? null,
          to: to ?? null,
          source: source ?? initialState.filters.source,
        },
      };
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSelectedUserNames: (state, action) => {
      state.selectedUserNames = action.payload ?? [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceHeader.pending, (state) => {
        state.headerIsLoading = true;
        state.headerError = null;
      })
      .addCase(fetchWorkspaceHeader.fulfilled, (state, action) => {
        if (!isForCurrentWorkspace(state, action)) {
          return;
        }
        state.headerIsLoading = false;
        state.header = action.payload;
      })
      .addCase(fetchWorkspaceHeader.rejected, (state, action) => {
        if (!isForCurrentWorkspace(state, action)) {
          return;
        }
        state.headerIsLoading = false;
        state.headerError = action.error?.message ?? DEFAULT_HEADER_ERROR;
      })
      .addCase(fetchWorkspaceByArea.fulfilled, (state, action) => {
        if (!isForCurrentWorkspace(state, action)) {
          return;
        }
        state.byArea = action.payload;
      })
      .addCase(fetchWorkspaceByUser.fulfilled, (state, action) => {
        if (!isForCurrentWorkspace(state, action)) {
          return;
        }
        state.byUser = action.payload;
      })
      .addCase(exportWorkspaceByAreaThunk.pending, (state) => {
        state.isExporting = true;
      })
      .addCase(exportWorkspaceByAreaThunk.fulfilled, (state) => {
        state.isExporting = false;
      })
      .addCase(exportWorkspaceByAreaThunk.rejected, (state) => {
        state.isExporting = false;
      })
      .addCase(exportWorkspaceByUserThunk.pending, (state) => {
        state.isExporting = true;
      })
      .addCase(exportWorkspaceByUserThunk.fulfilled, (state) => {
        state.isExporting = false;
      })
      .addCase(exportWorkspaceByUserThunk.rejected, (state) => {
        state.isExporting = false;
      });
  },
});

export const { resetForWorkspace, setFilters, setSelectedUserNames } =
  cloudUsageWorkspaceDetailSlice.actions;
export default cloudUsageWorkspaceDetailSlice.reducer;
